/**
 * Where things are on the board.
 *
 * Connectors have to know how big an item is to meet its edge, and the add
 * handles have to know how far to place a sibling. Both answers live here so
 * the canvas, the connector layer and the handles cannot disagree about it.
 */

/** Items whose size is fixed by their component rather than by the model. */
const NOMINAL = {
  sticky: { width: 168, height: 168 },
  // Text grows with what is typed; this is the empty height, which is close
  // enough for an anchor point and keeps the maths from needing the DOM.
  text: { width: 150, height: 40 },
  // A dropped question: its width, and a typical height for a few lines.
  question: { width: 380, height: 200 },
  content: { width: 420, height: 594 },
};

/**
 * An item's box. Its own size wins — a note that has been resized is the
 * size it was made — and the nominal size stands in for one never touched.
 */
export const boundsOf = (item) => {
  const nominal = NOMINAL[item.kind];
  return {
    x: item.x,
    y: item.y,
    width: Math.max(item.width || (nominal && nominal.width) || 0, 1),
    height: Math.max(item.height || (nominal && nominal.height) || 0, 1),
  };
};

/** The smallest an item can be resized to, so it cannot vanish under a drag. */
export const MIN_SIZE = { sticky: 64, shape: 12, text: 60, content: 120 };

/** Which kinds can be resized, and from which handles. */
export const RESIZABLE = new Set(["sticky", "shape", "text", "content"]);

export const centreOf = (item) => {
  const b = boundsOf(item);
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
};

/**
 * Where a line leaving `item` towards `target` crosses the item's edge.
 *
 * Scaling the direction vector until it touches the nearer pair of sides is
 * what keeps a connector pointing at the centre while stopping at the border,
 * so it never disappears under the note it belongs to.
 */
export const edgePointTowards = (item, target) => {
  const b = boundsOf(item);
  const cx = b.x + b.width / 2;
  const cy = b.y + b.height / 2;
  const dx = target.x - cx;
  const dy = target.y - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const t = 1 / Math.max(Math.abs(dx) / (b.width / 2), Math.abs(dy) / (b.height / 2));
  return { x: cx + dx * t, y: cy + dy * t };
};

/** The gap left between an item and the sibling a handle spawns. */
export const SPAWN_GAP = 56;

export const DIRECTIONS = [
  { id: "up", label: "Add above", dx: 0, dy: -1 },
  { id: "right", label: "Add to the right", dx: 1, dy: 0 },
  { id: "down", label: "Add below", dx: 0, dy: 1 },
  { id: "left", label: "Add to the left", dx: -1, dy: 0 },
];

/** Where a sibling goes when a handle on `item` is clicked. */
export const spawnPosition = (item, direction) => {
  const b = boundsOf(item);
  return {
    x: b.x + direction.dx * (b.width + SPAWN_GAP),
    y: b.y + direction.dy * (b.height + SPAWN_GAP),
  };
};

/** Which kinds can be chained with the add handles. */
export const CONNECTABLE = new Set(["sticky", "shape"]);

export const isConnector = (item) => item.kind === "connector";
